import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { SentryErrorBoundary } from '@services/SentryService';

interface PatientDeduplicationErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Error boundary for the Patient Deduplication feature.
 * Prevents rendering errors from crashing the entire page
 * and reports them to Sentry with feature-specific context.
 */
export const PatientDeduplicationErrorBoundary: React.FC<
  PatientDeduplicationErrorBoundaryProps
> = ({ children }) => {
  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
          <Alert
            severity="error"
            icon={<ErrorOutlineIcon />}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => resetError()}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            <AlertTitle>Error Loading Patient Deduplication</AlertTitle>
            <Typography variant="body2">
              Something went wrong while loading the deduplication tool. Please try again.
            </Typography>
            {process.env.NODE_ENV === 'development' && error instanceof Error && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: 'block', fontFamily: 'monospace' }}
              >
                {error.message}
              </Typography>
            )}
          </Alert>
        </Box>
      )}
      beforeCapture={scope => {
        scope.setTag('component', 'PatientDeduplicationPage');
        scope.setTag('feature', 'patient-deduplication');
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
};
